import errorTypes from '../../constants/errorTypes';
import { packageModel } from '../../models/package';
import { fetchHotels as fetchCombinationHotels } from '../../services/combination';
import { fetchLabels } from '../../services/content';
import { fetchFlightTime } from '../../services/flight';
import { fetchHotels, fetchPriceCalendar, fetchUpsell } from '../../services/package';
import { logSearch } from '../../utils/logUtils/CabinListLogUtils';
import * as ExponeaUtils from '../../utils/logUtils/ExponeaUtils';
import HotelListLogUtils from '../../utils/logUtils/HotelListLogUtils';
import { getProductsIncluded } from '../../utils/mappers/productsIncluded';
import { urlUtils } from '../../utils/url';

import { createAction } from '@reduxjs/toolkit';

// **Every action here is shared, thunk-dispatched, or both**, so none can be slice-generated: the
// slice would import this module while this module imported the slice, and a `createSlice`
// evaluated inside that cycle reads `undefined`. `hotelSearchSucceeded` alone is read by nine
// slices. `sortOrderSelected` is read by two *and* wrapped by `sortHotels` below, which performs
// side effects a `createSlice` creator cannot.
//
// The five filter actions that no thunk dispatches — the panel toggles, the two clears and the
// option update — are slice-generated on `reducers/hotelList/filterReducer.js` instead.
//
// **The payload moved.** The old module mixed two conventions: hand-written creators put their
// fields at the action's top level, while the `actionCreator` factory nested them under `data`.
// Both are `action.payload` now. `actionCreator` also filtered the payload to its declared props
// and defaulted `hits` to 0; nothing ever read `hits`, and no caller passes an undeclared field,
// so dropping that filtering changes nothing that is read.
export const initStarted = createAction('hotelList/initStarted');
export const initSucceeded = createAction('hotelList/initSucceeded');
export const hotelSearchStarted = createAction('hotelList/hotelSearchStarted');
export const hotelSearchSucceeded = createAction('hotelList/hotelSearchSucceeded');
export const hotelSearchFailed = createAction('hotelList/hotelSearchFailed');
export const hotelsRefetched = createAction('hotelList/hotelsRefetched');
export const priceCalendarSucceeded = createAction('hotelList/priceCalendarSucceeded');
export const offerPriceUpdated = createAction('hotelList/offerPriceUpdated');
export const sortOrderSelected = createAction('hotelList/sortOrderSelected');
export const flightTimeSucceeded = createAction('hotelList/flightTimeSucceeded');
export const messageAdded = createAction('hotelList/messageAdded');
export const errorPopupToggled = createAction('hotelList/errorPopupToggled');

// No slice reads this one at all — only the query listener, which writes the scroll offset into
// the URL so back-navigation can restore it. It lives here rather than on a slice because there
// is no slice that owns it.
export const scrollPositionSaved = createAction('hotelList/scrollPositionSaved');

let refetchHotelsAbortController = null;

const priceCalendarSearchSuccess = (calendarData, flatOffers, priceCalendarOffersHasPrice) =>
  priceCalendarSucceeded({
    flatOffers,
    priceCalendar: calendarData?.result,
    priceCalendarOffersHasPrice,
  });

const hotelSearchSuccess = (data, sortKey) => {
  const hotels = {};

  (data.result.hotels || []).forEach((hotel) => {
    hotels[hotel.id] = hotel;
  });

  const selectedHotel = data?.result?.selectedHotel;

  if (selectedHotel?.id) {
    hotels[selectedHotel.id] = selectedHotel;
  }

  return hotelSearchSucceeded({
    combinationInfo: data?.result?.combinationInformation,
    hotelIdList: (data.result.hotels || []).map((hotel) => `${hotel.id}`),
    hotelList: data.result.hotels,
    hotels,
    initialized: true,
    loggingInformation: data.result.loggingData,
    searchInformation: data.result.searchInformation,
    selectedHotel: selectedHotel?.id,
    sortBy: sortKey,
    summaryReturnLink: data?.result?.summaryReturnLink,
    transportInfo: data.result.transport,
    upsellMessage: data?.result?.upsellMessage,
  });
};

const addMessage = (data) => messageAdded({ translatedMessages: data.translatedMessage });

const hotelSearchError = (data) => {
  return (dispatch) => {
    if (data?.translatedMessage) {
      dispatch(addMessage(data));
    }

    dispatch(
      hotelSearchFailed({
        initialized: false,
        searchInformation: data?.result?.searchInformation,
      }),
    );
  };
};

export const toggleErrorPopup = (error) => errorPopupToggled({ ...error });

const getHotels = async (siteSettings, userId, dispatch, signal = null) => {
  dispatch(hotelSearchStarted());

  if (urlUtils.isUpsell()) {
    return fetchUpsell(siteSettings, userId);
  }

  if (urlUtils.isCombination()) {
    return fetchCombinationHotels(siteSettings, userId);
  }

  return fetchHotels(siteSettings, userId, signal);
};

const getPriceCalendar = async (siteSettings) => {
  return fetchPriceCalendar(siteSettings);
};

const getAllLabels = async (siteSettings) => {
  const labels = await fetchLabels(siteSettings, 'iwHotelList');
  const { general, filterLabels, ...rest } = labels;
  return {
    ...rest,
    ...filterLabels,
    ...general,
  };
};

// The log call and the URL write happen when this is *called*, before the action reaches the
// reducers. That is where they were, so that is where they stay — and it is why
// `sortOrderSelected` is a `createAction` here rather than generated by the `sort` slice.
export const sortHotels = (sortBy) => {
  HotelListLogUtils.sortHotelList(sortBy);
  urlUtils.updateUrlWithSortKey(sortBy);
  return sortOrderSelected({ sortBy });
};

const hasHotels = (data) => data?.result?.hotels?.length > 0 || data?.result?.selectedHotel;
const hasTransportInfo = (data) => data?.result?.transport;

const findOfferForDay = (offers, day) => offers?.find((o) => o.day === day);

const getLowestHotelPrice = (hotels = []) => {
  const prices = hotels.map((hotel) => hotel?.priceInfo?.totalPrice).filter((p) => p != null && p > 0);
  return prices.length > 0 ? Math.min(...prices) : null;
};

// Dispatches UPDATE_OFFER_PRICE when the calendar offer price differs from the
// lowest hotel price, returning logging data describing the change (or null).
const updateOfferPrice = (dispatch, { offer, offerDay, hotels }) => {
  const lowestPrice = getLowestHotelPrice(hotels);

  if (lowestPrice == null || offer?.totalPrice === lowestPrice) {
    return null;
  }

  dispatch(
    offerPriceUpdated({
      day: offerDay,
      totalPrice: lowestPrice,
    }),
  );

  return {
    eventAction: 'price_change',
    eventInfo: `${offer?.totalPrice};${lowestPrice}`,
  };
};

const dispatchSearchResults = (dispatch, data, sortKey) => {
  if (hasHotels(data)) {
    dispatch(hotelSearchSuccess(data, sortKey));
  }

  if (!hasHotels(data) || !hasTransportInfo(data)) {
    dispatch(hotelSearchError(data));
  }

  if (data?.translatedMessage) {
    dispatch(addMessage(data));
  }

  if (data?.result?.serviceCallInformation?.sessionHasExpired) {
    dispatch(
      toggleErrorPopup({
        visible: true,
        errorType: errorTypes.SESSION_EXPIRED,
        buttonUrl: urlUtils.isCombination() ? data?.result?.summaryNoSessionLink : null,
      }),
    );
  }
};

const finalizeSearchSuccess = (dispatch, labels, data, loggingData) => {
  if (loggingData) {
    // Log to exponea
    ExponeaUtils.logSearch(loggingData, 'Independent/PackageIndependentAccomodationList');

    if (urlUtils.isCruise()) {
      logSearch(loggingData, data?.result?.cruise);
    } else {
      HotelListLogUtils.searchHotels(loggingData, data?.translatedMessage, data?.result?.upsellMessage);
    }
  }

  if (hasHotels(data)) {
    // Product impressions needs to be pushed to dataLayer AFTER searchHotels() above
    HotelListLogUtils.logProductImpression(
      data?.result?.hotels,
      data?.result?.selectedHotel,
      data?.result?.loggingData,
    );
  }

  dispatch(
    initSucceeded({
      labels: {
        ...labels,
        productsIncludedParsed: getProductsIncluded(data?.result?.packageTag, labels),
      },
    }),
  );
};

const initSuccess = (labels, priceCalendarData, data) => {
  return (dispatch) => {
    const shouldUpdateUrl = data.result != null;
    const sortKey = urlUtils.getSortKeyFromUrl();
    const { calendars, priceCalendarOffersHasPrice } = priceCalendarData?.result || {};
    const thereIsPriceCalendarData = calendars?.length > 0;
    const sessionHasExpired = data?.result?.serviceCallInformation?.sessionHasExpired;

    // NB: updateUrlWithSearchResultParams only applies to flight data
    if (hasTransportInfo(data) && shouldUpdateUrl && !sessionHasExpired) {
      urlUtils.updateUrlWithSearchResultParams(data.result, sortKey);
    }

    let flatOffers = [];
    if (thereIsPriceCalendarData) {
      flatOffers = calendars.flatMap((cal) => cal.offers);
      dispatch(priceCalendarSearchSuccess(priceCalendarData, flatOffers, priceCalendarOffersHasPrice));
    }

    dispatchSearchResults(dispatch, data, sortKey);

    let loggingData = data?.result?.loggingData;

    // If the departure date exists and it is in the price calendar, update the
    // offer price when it differs from the lowest price in the hotels list.
    const { departureDate } = packageModel;
    if (departureDate && thereIsPriceCalendarData && priceCalendarOffersHasPrice) {
      const offer = findOfferForDay(flatOffers, departureDate);

      if (offer) {
        const priceChange = updateOfferPrice(dispatch, {
          offer,
          offerDay: departureDate,
          // if data?.result?.selectedHotel exists, add it to the hotels list so that the lowest price is calculated correctly
          hotels:
            data?.result?.hotels && data?.result?.selectedHotel
              ? [data.result.selectedHotel, ...data.result.hotels]
              : data?.result?.hotels,
        });
        if (priceChange) {
          loggingData = { ...loggingData, ...priceChange };
        }
      }
    }

    finalizeSearchSuccess(dispatch, labels, data, loggingData);
  };
};

const refetchHotelsSuccess = (labels, data, offerDay, state) => {
  return (dispatch) => {
    const shouldUpdateUrl = data.result != null && !urlUtils.isUpsell();
    const sortKey = urlUtils.getSortKeyFromUrl();
    const sessionHasExpired = data?.result?.serviceCallInformation?.sessionHasExpired;

    if (hasTransportInfo(data) && shouldUpdateUrl && !sessionHasExpired) {
      urlUtils.updateUrlWithSearchResultParams(data.result, sortKey);
    }

    dispatchSearchResults(dispatch, data, sortKey);

    let loggingData = data?.result?.loggingData;

    // Update offer price if it was unavailable or if it is not the same as the lowest price in the hotels list.
    if (offerDay && state.priceCalendar?.priceCalendarOffersHasPrice) {
      const offer = findOfferForDay(state.priceCalendar?.calendarData?.offers, offerDay);
      const priceChange = updateOfferPrice(dispatch, { offer, offerDay, hotels: data?.result?.hotels });
      if (priceChange) {
        loggingData = { ...loggingData, ...priceChange };
      }
    }

    finalizeSearchSuccess(dispatch, labels, data, loggingData);
  };
};

export const refetchHotels = (offerDay) => async (dispatch, getState) => {
  const state = getState();
  const {
    labels,
    common: { siteSettings, userId },
  } = state;

  // Cancel any in-flight refetch so a slower earlier request can't overwrite
  // the result of a more recently selected carousel offer.
  if (refetchHotelsAbortController) {
    refetchHotelsAbortController.abort();
  }
  const controller = new AbortController();
  refetchHotelsAbortController = controller;

  dispatch(hotelsRefetched());

  try {
    const packages = await getHotels(siteSettings, userId, dispatch, controller.signal);

    dispatch(refetchHotelsSuccess(labels, packages, offerDay, state));
  } catch (error) {
    // A newer selection aborted this request; ignore it so the latest
    // request's result is the one that renders.
    if (error?.name === 'AbortError') {
      return;
    }
    throw error;
  } finally {
    if (refetchHotelsAbortController === controller) {
      refetchHotelsAbortController = null;
    }
  }
};

export const init = () => async (dispatch, getState) => {
  const {
    common: { siteSettings, userId },
  } = getState();
  const { siteId, experiments } = siteSettings;
  const showDPCarousel = experiments === 'dppricecalendarcarousel:showdpcarousel';

  // No reducer reads this. Kept, because removing it removes a store notification — the same
  // call combo-summary, price-matrix and flight-list made for their dead `INIT`.
  dispatch(initStarted());

  const [content, packages, priceCalendar] = await Promise.all([
    getAllLabels(siteSettings),
    getHotels(siteSettings, userId, dispatch),
    // Fetch price calendar only for VingSE (siteId = 1) and if the DPCarousel experiment is enabled.
    siteId === 1 && showDPCarousel ? getPriceCalendar(siteSettings) : Promise.resolve(null),
  ]);

  dispatch(initSuccess(content, priceCalendar, packages));
};

export const getFlightTime = (homeboundTransportKey, outboundTransportKey) => async (dispatch, getState) => {
  const { siteSettings } = getState().common;
  const sessionId = urlUtils.getSessionIdFromUrl();

  const response = await fetchFlightTime(sessionId, homeboundTransportKey, outboundTransportKey, siteSettings);

  dispatch(flightTimeSucceeded({ flightTimeInformation: response?.result }));
};

// Filter Actions
//
// The five filter actions no thunk dispatches — `updateFilterOption`, the two panel toggles and
// the two clears — are generated by the `filter` slice that owns them, and consumers import them
// from `reducers/hotelList/filterReducer` directly, as price-matrix and cruise-list do with
// theirs.
//
// **They are deliberately not re-exported from here.** A re-export would make this module import
// the slice while the slice imports this module for its shared actions, which is precisely the
// cycle that leaves a `createSlice` reading `undefined`.
//
/*
  NOTE:
  A checkbox will be toggled through: { filterId, optionId, value }.
  A single slider can reuse this approach: { filterId, optionId=SUP, value }
  A double slider can also use it: { filterId, optionId=SUP/INF, value },
*/
